// N38 · Phase 4.1 — 作息剧本：时段 + 上一件已打卡事 → 推荐下一步（纯本地规则，不做历史学习）
//
// 规则格式（首条命中即用）：
//   { when: [起分钟, 止分钟), after: ['分类id或root id'] | null, suggest: ['分类id', ...] }
//   - after=null 表示不看上一件事（兜底规则放各时段最后）
//   - after 匹配「上一件事的 catId 或它的 root」（如 'sleep' 能命中 sleep_lazy 之外的整个睡眠族）
// 可手改：localStorage.checkinScript 存同格式 JSON 数组即可整体覆盖（坏数据自动回退默认）。

import { SLOTS } from '../data/slots.js';
import { rootOf } from './activityMatcher.js';

const KEY = 'checkinScript';

// 默认剧本（按立项的早起序列：醒→赖床/拉/洗漱→吃饭→…；全天 0–24 点有兜底）
export const DEFAULT_SCRIPT = [
  // 早晨 5:00–10:30（注意顺序：sleep_lazy 的 root 也是 sleep，专用规则必须排在 sleep 族规则前面）
  { when: [300, 630], after: ['sleep_lazy'], suggest: ['fun_s', 'exer_wash', 'eat'] },
  { when: [300, 630], after: ['sleep', 'sleep_phone'], suggest: ['sleep_lazy', 'fun_s', 'exer_wash'] },
  { when: [300, 630], after: ['fun_s', 'exer_wash', 'exer_hair'], suggest: ['eat'] },
  { when: [300, 630], after: ['eat'], suggest: ['prep_xnx', 'focus_work', 'exer_run'] },
  { when: [300, 630], after: null, suggest: ['sleep_lazy', 'exer_wash', 'eat'] },
  // 上午 10:30–11:30
  { when: [630, 690], after: null, suggest: ['focus_work', 'focus_en', 'prep_pr'] },
  // 中午 11:30–13:30
  { when: [690, 810], after: ['eat'], suggest: ['sleep', 'fun_book', 'fun_relax'] },
  { when: [690, 810], after: null, suggest: ['eat'] },
  // 公园时间 16:00–18:00（用户 2026-06-06 写入；排在下午/傍晚规则前面才能在重叠时段赢）
  { when: [960, 1080], after: null, suggest: ['exercise', 'exer_run'] },
  // 下午 13:30–17:00
  { when: [810, 1020], after: ['sleep'], suggest: ['focus_work', 'focus_en', 'prep_pr'] },
  { when: [810, 1020], after: null, suggest: ['focus_work', 'fun_book', 'exercise'] },
  // 傍晚 17:00–19:30
  { when: [1020, 1170], after: ['eat'], suggest: ['fun_relax', 'exer_run', 'social_talk'] },
  { when: [1020, 1170], after: null, suggest: ['eat'] },
  // 晚上 19:30–22:00
  { when: [1170, 1320], after: null, suggest: ['exer_foot', 'exer_wash', 'fun_book'] },
  // 睡前 22:00–24:00
  { when: [1320, 1440], after: null, suggest: ['sleep', 'exer_foot'] },
  // 凌晨 0:00–5:00
  { when: [0, 300], after: null, suggest: ['sleep'] },
];

// 排除约束（用户拍板，全时段生效）：上一件事（catId 或其 root）→ 不许出现在推荐里的分类。
// 2026-06-06：上一件是睡眠（族）→ 不推泡脚（都睡了还泡什么脚）
export const EXCLUDE_AFTER = {
  sleep: ['exer_foot'],
};

export function loadScript() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_SCRIPT;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.every(r => Array.isArray(r.when) && Array.isArray(r.suggest))) return arr;
  } catch (e) { /* 坏数据回退默认 */ }
  return DEFAULT_SCRIPT;
}

// 推荐下一步：首条命中规则的 suggest（剔掉和上一件事相同的分类 + 排除约束，最多 4 个）
export function recommendNext(nowMin, prevCatId, script) {
  const rules = script || loadScript();
  const root = prevCatId ? rootOf(prevCatId) : null;
  const banned = [...(EXCLUDE_AFTER[prevCatId] || []), ...(root && root !== prevCatId ? EXCLUDE_AFTER[root] || [] : [])];
  for (const r of rules) {
    if (!(nowMin >= r.when[0] && nowMin < r.when[1])) continue;
    if (r.after && !(r.after.includes(prevCatId) || r.after.includes(root))) continue;
    return r.suggest.filter(id => id !== prevCatId && !banned.includes(id)).slice(0, 4);
  }
  return [];
}

// ---- slot 工具：当前格边界 / 上一件已打卡事 ----
const BOUNDS = SLOTS.map(s => {
  const start = s.h * 60 + s.m;
  return { start, end: start + (s.span === 0.5 ? 30 : 60) };
});

export function slotIdxAt(min) {
  const m = ((min % 1440) + 1440) % 1440;
  for (let i = 0; i < BOUNDS.length; i++) if (m >= BOUNDS[i].start && m < BOUNDS[i].end) return i;
  return -1;
}

// 当前所在格的边界（推荐一键打卡按"单点占一格"口径记这一格）
export function currentSlotBounds(nowMin) {
  const idx = slotIdxAt(nowMin);
  return idx < 0 ? null : { idx, startMin: BOUNDS[idx].start, endMin: BOUNDS[idx].end };
}

// 上一件已打卡事：从当前格往前扫，三轨中第一个非空格（含当前格——正在做的事也算）
export function findPrevActivity(day, nowMin) {
  if (!day || !day.tracks) return null;
  const cur = slotIdxAt(nowMin);
  for (let i = cur; i >= 0; i--) {
    for (let t = 0; t < 3; t++) {
      const c = day.tracks[t] && day.tracks[t][i];
      if (c != null) return { catId: c, idx: i, track: t };
    }
  }
  return null;
}
